<script setup>
import { computed, defineEmits, defineProps, ref, watch } from 'vue'
import { useStore } from 'vuex'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import CellBoardRow from '@/components/panels/CellBoardRow.vue'
import { errorNotify, prettifyDateTime, successNotify } from '@/mixins'

// Panel of an out of service cell: resolve the content left in it (Admin/Manager),
// then bring the cell back into service (Admin). The parent refetches the device
// on `changed` — the cell status and its active_order move with every step.
const props = defineProps({
  open: { type: Boolean, default: false },
  cell: { type: Object, default: null },
  // the manual processing order of this cell: the active one, or the last resolved one
  order: { type: Object, default: null },
  orderLoading: { type: Boolean, default: false },
  canResolve: { type: Boolean, default: false },
  canActivate: { type: Boolean, default: false },
  // a board fault is one way into out of service, so the board can be replaced from here too
  inMaintenance: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'changed', 'maintenance'])
const store = useStore()

const RESOLUTIONS = [
  { value: 'kept_in_cell', label: 'Item stays in the cell', hint: 'The rental continues' },
  { value: 'returned_to_owner', label: 'Returned to the owner' },
  { value: 'handed_to_support', label: 'Handed to support', comment: true },
  { value: 'moved_to_cell', label: 'Moved to another cell', comment: true, placeholder: 'Which cell?' },
  { value: 'lost_or_damaged', label: 'Lost or damaged', comment: true },
  { value: 'cell_was_empty', label: 'The cell was empty' },
]
const COMMENT_MAX = 512
const resolutionOf = (value) => RESOLUTIONS.find((r) => r.value === value)

const resolution = ref('')
const comment = ref('')
const fieldErrors = ref({})
const activateError = ref('')
const submitting = ref(false)
const confirmOpen = ref(false)

watch(() => [props.open, props.cell?.number], () => {
  resolution.value = ''
  comment.value = ''
  fieldErrors.value = {}
  activateError.value = ''
  confirmOpen.value = false
})

const hasActiveOrder = computed(() => !!props.cell?.active_order)
// an active order counts as unresolved until it is loaded and says otherwise
const needsResolution = computed(() => hasActiveOrder.value && !props.order?.resolution)
const picked = computed(() => resolutionOf(resolution.value))
const commentRequired = computed(() => !!picked.value?.comment)
const canSubmit = computed(() =>
  !!resolution.value
  && !(commentRequired.value && !comment.value.trim())
  && comment.value.length <= COMMENT_MAX
  && !submitting.value)

const outcome = computed(() => (props.order?.resolution ? resolutionOf(props.order.resolution) : null))
const keepsItem = computed(() => hasActiveOrder.value && props.order?.resolution === 'kept_in_cell')
const confirmMessage = computed(() => (keepsItem.value
  ? `The item stays in cell ${props.cell?.number}: the rental continues and the cell becomes Occupied.`
  : `Cell ${props.cell?.number} becomes Available for new rentals.`))

// DRF validation errors are lists per field; anything else is a display-ready detail
const asText = (v) => (Array.isArray(v) ? v.join(' ') : String(v))

function saveResolution() {
  if (!canSubmit.value) return
  submitting.value = true
  fieldErrors.value = {}
  const data = { resolution: resolution.value, comment: comment.value.trim() }
  store.dispatch('order/resolveOrder', { id: props.cell.active_order, data })
    .then(() => {
      const error = store.getters['order/resolveOrderError']
      if (!error) {
        successNotify('Resolution saved')
        emit('changed')
        return
      }
      const { resolution: resErr, comment: comErr, ...rest } = error
      fieldErrors.value = {
        ...(resErr ? { resolution: asText(resErr) } : {}),
        ...(comErr ? { comment: asText(comErr) } : {}),
      }
      if (Object.keys(rest).length) errorNotify(rest)
    })
    .finally(() => { submitting.value = false })
}

function activate() {
  confirmOpen.value = false
  submitting.value = true
  activateError.value = ''
  store.dispatch('cell/activateCell', props.cell.id)
    .then(() => {
      const error = store.getters['cell/cellActivateError']
      if (!error) {
        successNotify(`Cell ${props.cell.number} is back in service`)
        emit('changed')
        emit('close')
        return
      }
      activateError.value = error.detail ? asText(error.detail) : Object.values(error).map(asText).join(' ')
    })
    .finally(() => { submitting.value = false })
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open && cell" class="modal-overlay" @click.self="emit('close')">
      <div class="modal modal--form oos-panel">
        <div class="modal__formhead">
          <h3 class="modal__title">Cell {{ cell.number }}</h3>
          <span class="pill pill--oos"><span class="mdi mdi-alert-octagon-outline"></span> Out of service</span>
          <button class="modal__close" type="button" @click="emit('close')">
            <span class="mdi mdi-close"></span>
          </button>
        </div>

        <div class="oos-body">
          <div v-if="hasActiveOrder" class="oos-order">
            <span class="mdi mdi-clipboard-text-outline"></span>
            <span>Order</span>
            <router-link class="mono strong" :to="{ name: 'order-detail', params: { id: cell.active_order } }">
              #{{ cell.active_order }}
            </router-link>
            <span v-if="needsResolution" class="pill pill--maint">Needs resolution</span>
          </div>

          <div v-if="needsResolution && orderLoading" class="oos-note">
            <span class="mdi mdi-loading mdi-spin"></span> Loading the order…
          </div>

          <!-- step 1: resolve the content -->
          <template v-else-if="needsResolution">
            <template v-if="canResolve">
              <div class="field">
                <span class="field__label">What happened to the content? <i>*</i></span>
                <div class="oos-options">
                  <label
                    v-for="r in RESOLUTIONS"
                    :key="r.value"
                    class="oos-option"
                    :class="{ on: resolution === r.value }">
                    <input v-model="resolution" type="radio" name="resolution" :value="r.value">
                    <span class="oos-option__t">
                      {{ r.label }}
                      <em v-if="r.hint">{{ r.hint }}</em>
                      <em v-else-if="r.comment">comment required</em>
                    </span>
                  </label>
                </div>
                <span v-if="fieldErrors.resolution" class="oos-err">{{ fieldErrors.resolution }}</span>
              </div>

              <label class="field">
                <span class="field__label">
                  Comment
                  <i v-if="commentRequired"> *</i>
                  <em v-else class="field__opt"> optional</em>
                </span>
                <textarea
                  v-model="comment"
                  class="field__input field__textarea"
                  :class="{ 'is-invalid': fieldErrors.comment || comment.length > COMMENT_MAX }"
                  rows="3"
                  :maxlength="COMMENT_MAX"
                  :placeholder="picked?.placeholder || ''"></textarea>
                <span class="oos-counter">{{ comment.length }} / {{ COMMENT_MAX }}</span>
                <span v-if="fieldErrors.comment" class="oos-err">{{ fieldErrors.comment }}</span>
              </label>
            </template>
            <div v-else class="oos-note">
              <span class="mdi mdi-lock-outline"></span>
              The content of this cell has to be resolved by an administrator or a manager.
            </div>
          </template>

          <!-- step 2: the resolution outcome, if any -->
          <template v-else>
            <div v-if="outcome" class="oos-outcome">
              <div class="oos-outcome__head">
                <span class="mdi mdi-check-circle-outline"></span>
                {{ hasActiveOrder ? 'Resolution' : 'Last resolution' }}: <b>{{ outcome.label }}</b>
              </div>
              <div class="oos-outcome__meta">
                <router-link
                  v-if="!hasActiveOrder"
                  class="mono"
                  :to="{ name: 'order-detail', params: { id: order.id } }">#{{ order.id }}</router-link>
                <span v-if="order.resolved_at">{{ prettifyDateTime(order.resolved_at) }}</span>
              </div>
              <div v-if="keepsItem" class="oos-outcome__hint">The rental continues once the cell is back in service.</div>
            </div>
            <div v-else class="oos-note">
              <span class="mdi mdi-information-outline"></span>
              No order was left in this cell — it can go straight back into service.
            </div>
          </template>

          <CellBoardRow
            :cell="cell"
            :can-replace="canActivate"
            :in-maintenance="inMaintenance"
            @changed="emit('changed')"
            @maintenance="emit('maintenance')" />

          <div v-if="canActivate && needsResolution" class="oos-hint">
            <span class="mdi mdi-power"></span> Resolve the cell content first — then it can return to service
          </div>

          <div v-if="activateError" class="oos-alert">
            <span class="mdi mdi-alert-circle-outline"></span> {{ activateError }}
          </div>
        </div>

        <div class="modal__actions modal__actions--right">
          <button class="btn-ghost" type="button" @click="emit('close')">Close</button>
          <button
            v-if="needsResolution && canResolve && !orderLoading"
            class="btn-primary"
            type="button"
            :disabled="!canSubmit"
            @click="saveResolution">
            <span v-if="submitting" class="mdi mdi-loading mdi-spin"></span>
            Save resolution
          </button>
          <span
            v-if="canActivate"
            class="oos-activate"
            :title="needsResolution ? 'Resolve the cell content first' : ''">
            <button
              class="btn-primary"
              type="button"
              :disabled="needsResolution || submitting"
              @click="confirmOpen = true">
              <span class="mdi mdi-power"></span>
              Return to service
            </button>
          </span>
        </div>
      </div>
    </div>
  </Teleport>

  <ConfirmModal
    :open="confirmOpen"
    :title="`Return cell ${cell?.number} to service?`"
    :message="confirmMessage"
    confirm-label="Return to service"
    icon="power"
    @confirm="activate"
    @cancel="confirmOpen = false" />
</template>
